import apiClient from './apiClient';
import { User, UserRole } from '../types/auth';

// Re-export User type
export type { User };

export interface LeaderboardUser {
  id: number;
  username?: string;
  firstName: string;
  lastName: string;
  rank: number;
  totalCorrect: number;
  accuracy: number;
  problemsSolved: number;
  achievementCount: number;
  showAchievements: boolean;
  profileColor?: string; // Added profile color for custom avatar colors
  totalPoints: number; // Total points earned using 1/attempts scoring
}

export interface UserListParams {
  page?: number;
  limit?: number;
  role?: UserRole;
  search?: string;
}

interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
}

interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  profile?: {
    school?: string;
    grade?: number;
    parentEmail?: string;
    phone?: string;
  };
}

interface UpdateUserData {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  profile?: {
    school?: string;
    grade?: number;
    parentEmail?: string;
    phone?: string;
    username?: string;
    showAchievements?: boolean;
    profileColor?: string;
  };
}

// Get all users (admin only) with pagination and filtering
export const getUsers = async (params: UserListParams = {}): Promise<UserListResponse> => {
  try {
    const response = await apiClient.get('/api/users', { params });
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch users');
  }
};

// Get a specific user by ID
export const getUserById = async (userId: number): Promise<User> => {
  try {
    const response = await apiClient.get(`/api/users/${userId}`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch user');
  }
};

// Create a new user (admin only)
export const createUser = async (userData: CreateUserData): Promise<User> => {
  try {
    const response = await apiClient.post('/api/users', userData);
    return response.data;
  } catch (error) {
    throw new Error('Failed to create user');
  }
};

// Update a user
export const updateUser = async (userId: number, userData: UpdateUserData): Promise<User> => {
  try {
    const response = await apiClient.put(`/api/users/${userId}`, userData);
    return response.data.user; // Extract the user object from the wrapper
  } catch (error) {
    throw new Error('Failed to update user');
  }
};

// Delete a user
export const deleteUser = async (userId: number): Promise<{success: boolean, message: string}> => {
  try {
    const response = await apiClient.delete(`/api/users/${userId}`);
    
    // Process the response to check if it was successful
    if (response.data && response.data.success) {
      return { 
        success: true, 
        message: response.data.message || 'User deleted successfully' 
      };
    } else {
      // If the API doesn't return a clear success flag, assume success based on status
      return {
        success: true,
        message: 'User deleted successfully'
      };
    }
  } catch (error: unknown) {
    console.error('Error deleting user:', error);
    // Extract error message from the API response if available
    const err = error as { response?: { data?: { message?: string } } };
    const errorMessage = err.response?.data?.message || 'Failed to delete user';
    throw new Error(errorMessage);
  }
};

// Change password
export const changePassword = async (userId: number, currentPassword: string, newPassword: string): Promise<void> => {
  try {
    await apiClient.post(`/api/users/${userId}/change-password`, {
      currentPassword,
      newPassword,
    });
  } catch (error) {
    throw new Error('Failed to change password');
  }
};

// Reset user password (admin only)
export const resetUserPassword = async (userId: number, newPassword: string): Promise<void> => {
  try {
    await apiClient.post(`/api/users/${userId}/reset-password`, {
      newPassword
    });
  } catch (error) {
    throw new Error('Failed to reset user password');
  }
};

// Get overall leaderboard across all sessions
export const getLeaderboard = async (page = 1, limit = 20): Promise<{users: LeaderboardUser[], total: number}> => {
  try {
    const response = await apiClient.get('/api/users/leaderboard', { 
      params: { page, limit } 
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch leaderboard:', error);
    throw new Error('Failed to fetch leaderboard');
  }
};

// Get session-specific leaderboard
export const getSessionLeaderboard = async (
  sessionId: number,
  page = 1, 
  limit = 20
): Promise<{users: LeaderboardUser[], total: number}> => {
  try {
    const response = await apiClient.get(`/api/users/leaderboard/session/${sessionId}`, { 
      params: { page, limit } 
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch leaderboard for session ${sessionId}:`, error);
    throw new Error('Failed to fetch session leaderboard');
  }
};

// Get user profile for leaderboard
export const getUserLeaderboardProfile = async (
  userId: number, 
  options?: { sessionId?: number }
): Promise<LeaderboardUser> => {
  try {
    const params = options?.sessionId ? { sessionId: options.sessionId } : undefined;
    const response = await apiClient.get(`/api/users/${userId}/leaderboard-profile`, { params });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch user profile for ${userId}:`, error);
    throw new Error('Failed to fetch user profile');
  }
};

// For super admins to switch to admin role
export const switchToAdminRole = async (tenantId?: number): Promise<{token: string}> => {
  try {
    const response = await apiClient.post('/api/tenants/switch-to-admin', {
      tenantId: tenantId
    });
    return response.data;
  } catch (error) {
    console.error('Failed to switch to admin role:', error);
    throw new Error('Failed to switch roles');
  }
};

// For super admins to switch back to super admin role
export const switchToSuperAdminRole = async (): Promise<{token: string}> => {
  try {
    const response = await apiClient.post('/api/tenants/switch-to-super-admin');
    return response.data;
  } catch (error) {
    console.error('Failed to switch back to super admin role:', error);
    throw new Error('Failed to switch roles');
  }
};